/**
 * Banker Recon manual adjustments: reviewer-entered amounts inside the warehouse wire that the
 * importers do not cover (e.g. property taxes the warehouse withheld at closing and returned in the
 * net proceeds). Every entry moved through the warehouse wire:
 *
 * | Kind         | Plain meaning                              | Expected wire | Net profit |
 * |--------------|--------------------------------------------|---------------|------------|
 * | income       | Money we earned and keep                   | + amount      | + amount   |
 * | expense      | A company cost deducted from the wire      | − amount      | − amount   |
 * | pass_through | Money we receive and remit to someone else | + amount      | unchanged  |
 *
 * Entries persist as one ordinary recon line item each (no schema change). The line_type prefix is the
 * entry's identity; the category is derived from it on save (income and pass-through → `loan_revenue`,
 * expense → `lender_cost`). computeBankedRecon keeps pass-throughs out of revenue and costs, and the
 * wire decomposition shows them as clearing activity, like escrow. No entry nets to the warehouse
 * advance; the wire effect is a separate signed addition after the expected-wire calculation.
 */

export type ManualAdjustmentKind = 'income' | 'expense' | 'pass_through';

export interface ManualReconAdjustment {
  id: string;
  description: string;
  /** Positive dollars, cents precision. `kind` carries the effect. */
  amount: number;
  kind: ManualAdjustmentKind;
}

export const MANUAL_ADJUSTMENT_KIND_LABELS: Record<ManualAdjustmentKind, string> = {
  income: 'Income',
  expense: 'Expense',
  pass_through: 'Pass-through'
};

const KINDS: Record<ManualAdjustmentKind, {
  prefix: string;
  category: 'loan_revenue' | 'lender_cost';
  wireSign: 1 | -1;
}> = {
  income: { prefix: 'manual_adjustment_income_', category: 'loan_revenue', wireSign: 1 },
  expense: { prefix: 'manual_adjustment_expense_', category: 'lender_cost', wireSign: -1 },
  pass_through: { prefix: 'manual_adjustment_pass_through_', category: 'loan_revenue', wireSign: 1 }
};
// numeric(12,2) ceiling for banker_recon_line_items.amount
const MAX_AMOUNT = 1e10;

const cents = (value: number): number => Math.round(value * 100);

function manualKind(lineType: unknown): ManualAdjustmentKind | null {
  if (typeof lineType !== 'string') {
    return null;
  }
  return (Object.keys(KINDS) as ManualAdjustmentKind[]).find(kind => lineType.startsWith(KINDS[kind].prefix)) ?? null;
}

export function isManualAdjustmentLineType(lineType: unknown): boolean {
  return manualKind(lineType) !== null;
}

export function isManualPassThroughLineType(lineType: unknown): boolean {
  return manualKind(lineType) === 'pass_through';
}

/** The entry, or null when the description is blank or the amount is not a positive dollar figure. */
export function buildManualAdjustment(
  input: Omit<ManualReconAdjustment, 'id'>,
  id: string = crypto.randomUUID()
): ManualReconAdjustment | null {
  const description = input.description.trim();
  const amount = cents(input.amount) / 100;
  if (!description || !Number.isFinite(amount) || amount <= 0 || amount >= MAX_AMOUNT) {
    return null;
  }
  return { ...input, id, description, amount };
}

export function manualAdjustmentToLineItem(entry: ManualReconAdjustment) {
  const { prefix, category } = KINDS[entry.kind];
  return {
    category,
    line_type: `${prefix}${entry.id}`,
    description: entry.description,
    amount: entry.amount,
    source: 'manual' as const,
    reimbursable: false,
    nets_to_warehouse_advance: false,
    warehouse_retained_amount: 0
  };
}

/**
 * Saved line item → entry, with the kind taken from the line_type prefix; null for every non-manual
 * row. A row stored under the wrong category still loads, and the next save writes its canonical one.
 */
export function manualAdjustmentFromLineItem(item: {
  line_type: string;
  description?: string | null;
  amount: number | string;
}): ManualReconAdjustment | null {
  const kind = manualKind(item.line_type);
  if (!kind) {
    return null;
  }
  return {
    id: item.line_type.slice(KINDS[kind].prefix.length),
    description: item.description || '',
    amount: Number(item.amount),
    kind
  };
}

/** Signed effect on the expected warehouse wire: income and pass-throughs add, expenses subtract. */
export function manualWireAdjustment(
  items: ReadonlyArray<{ line_type: string; amount: number | string }> | null | undefined
): number {
  let total = 0;
  for (const item of items || []) {
    const kind = manualKind(item.line_type);
    if (kind) {
      total += KINDS[kind].wireSign * cents(Number(item.amount));
    }
  }
  return total / 100;
}
